/**
 * Proxy player. Positions are displayed as source timecode derived from
 * integer ticks and the source's rational frame rate. Browser seeking is
 * approximate (HTML media seeks to the nearest decodable frame); the UI says
 * so, and exports will follow the authoritative sequence timing server-side.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  AudioLines, ChevronLeft, ChevronRight, Maximize2, Minimize2, Pause, Play, Volume1, Volume2, VolumeX, X,
} from 'lucide-react';
import {
  formatDuration, framesToTimecode, formatTimecode, parseTimecode, secondsToTicks, ticksToFrames,
  type AssetDetail, type Cue, type Rational, type SpriteLayout, type TrackStyle,
} from '@prism/contracts';
import { isTyping } from '../lib/keys';
import { usePref } from '../lib/prefs';
import { Timeline } from './PlayerTimeline';
import { SubtitleOverlay } from './SubtitleOverlay';
import { useStableUrl } from './useStableUrl';
import { attachHls, usesNativeHls, type HlsAttachment } from './hls';
import { GO_LIVE_BACKOFF_SECONDS, atLiveEdge, behindSeconds, goLiveTarget } from './live-edge';
import { LiveEdgeButton } from './LiveEdgeButton';
import { programmeSeconds } from '../lib/timecode-read';
import { ShotJumpButtons, ShotTicks, ShowShotsToggle, useShotJumps } from './ShotTicks';
import { CueTicks } from './CueTicks';
import { STRIP_DEFAULT_SECONDS, STRIP_MAX_SECONDS, STRIP_MIN_SECONDS, SubtitleStrip } from '../subtitles/SubtitleStrip';
import type { SubtitleEditing } from '../subtitles/types';

export interface PlayerHandle {
  seek(seconds: number): void;
  element(): HTMLVideoElement | null;
  /** Current in/out marks (seconds), read synchronously at the moment of an edit. */
  range(): { in: number | null; out: number | null };
  /** Sets the in/out marks (seconds; null clears one), as I and O would (1.7.4: match frame). */
  setRange(inSec: number | null, outSec: number | null): void;
  /** Plays from one time to another and stops there (1.9.0: ⌥P plays the selected cue). */
  playRange(fromSec: number, toSec: number): void;
}

const SPEEDS = [0.25, 0.5, 1, 1.5, 2];

interface PlayerProps {
  asset: AssetDetail;
  /** Handle keyboard shortcuts (disable when another viewer is active). */
  keyboard?: boolean;
  /** Reports in/out marks in seconds of proxy/media time. */
  onRangeChange?: (range: { in: number | null; out: number | null }) => void;
  onTimeChange?: (seconds: number) => void;
  /** 1.9.3: playing or not (play, pause, the end), as ProgramViewer has it; the asset page's lists follow with it. */
  onPlayingChange?: (playing: boolean) => void;
  compact?: boolean;
  /** Selectable subtitle tracks, shown as an overlay (source-time cues). */
  subtitleTracks?: Array<{ id: string; label: string; language: string; cues: Cue[]; style?: TrackStyle | null }>;
  /**
   * While the Subtitles tab is open (1.9.0): the draft of the track being edited. The overlay shows it (without
   * writing the player.subtitles pref), the timeline shows its cues and the subtitle strip appears under the player.
   */
  subtitleEditing?: SubtitleEditing | null;
  /** Programme in and out (source timecode from the metadata), marked on the timeline. */
  programme?: { in: string | null; out: string | null };
  /** Shown when the person may change them: saves the marked in and out as the programme's. */
  onSetProgramme?: (marks: { in: string | null; out: string | null }) => void;
  /**
   * A recording still recording (1.0.0-rc.1): play the snapshot playlist of what is recorded so far instead of the
   * proxy (there is none yet). In and out can be marked within it; it is loaded again as it grows when the playhead
   * is near its end. 1.9.1: `followUrl` is the open playlist, played from Go live (or End) on while it follows the
   * live edge.
   */
  live?: { url: string; followUrl?: string | null; recordedSeconds: number; state: 'recording' | 'finishing' | 'finished' | 'failed' };
}

export const Player = forwardRef<PlayerHandle, PlayerProps>(function Player({ asset, keyboard = true, onRangeChange, onTimeChange, onPlayingChange, compact = false, subtitleTracks = [], programme, onSetProgramme, live, subtitleEditing = null }, ref) {
  const [subtitleId, setSubtitleId] = usePref<string>('player.subtitles', '');
  const [stripSeconds, setStripSeconds] = usePref<number>('player.subtitleStrip', STRIP_DEFAULT_SECONDS);
  const playUntil = useRef<{ to: number; stop: () => void } | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const playingCb = useRef(onPlayingChange);
  playingCb.current = onPlayingChange;
  useEffect(() => { playingCb.current?.(playing); }, [playing]);
  const [time, setTime] = useState(0);
  const [mediaDuration, setMediaDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = usePref('player.volume', 0.8);
  const [muted, setMuted] = usePref('player.muted', false);
  const [speed, setSpeed] = useState(1);
  const [inPoint, setInPoint] = useState<number | null>(null);
  const [outPoint, setOutPoint] = useState<number | null>(null);
  const [osd, setOsd] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(false);
  const reverseTimer = useRef<number | null>(null);
  const osdTimer = useRef<number | null>(null);

  const rate: Rational = asset.timing.sourceFrameRate ?? asset.frameRate ?? { num: 25, den: 1 };
  const startFrames = useMemo(
    () => (asset.timing.startTimecode ? parseTimecode(asset.timing.startTimecode, rate) ?? 0 : 0),
    [asset.timing.startTimecode, rate],
  );
  // A recording is as long as what it lists so far (the snapshot's own duration may round a little longer).
  const durationSec = live
    ? Math.min(mediaDuration || live.recordedSeconds, live.recordedSeconds || mediaDuration)
    : mediaDuration || (asset.durationTicks ?? 0) / 705_600_000;
  const frameSec = rate.den / rate.num;
  const isAudio = asset.mediaKind === 'audio';
  const proxyRep = asset.representations.find((r) => r.kind === 'proxy');
  const [proxySrc, refreshProxy] = useStableUrl(proxyRep?.id, asset.media.proxyUrl);
  const [posterSrc] = useStableUrl(asset.representations.find((r) => r.kind === 'thumbnail')?.id, asset.media.posterUrl);
  const [spriteSrc] = useStableUrl(asset.representations.find((r) => r.kind === 'sprite')?.id, asset.media.spriteUrl);
  const waveRep = asset.representations.find((r) => r.kind === 'waveform');
  const [waveSrc] = useStableUrl(waveRep?.id, asset.media.waveformUrl);
  const resumeAt = useRef<number | null>(null);

  // A recording plays through hls.js (Safari: its own HLS player).
  const hls = useRef<HlsAttachment | null>(null);
  const liveUrl = live?.url ?? null;
  const followUrl = live?.followUrl ?? null;
  const recordedSeconds = live?.recordedSeconds ?? 0;
  const recording = live?.state === 'recording';
  // 1.9.1: Go live (or End) switches once to the open playlist, which hls.js follows as it grows (no reloads), and
  // follows the live edge; pausing, scrubbing back or marking In or Out stops following (it stays on the open
  // playlist, which seeks like the snapshot). Without the open playlist, the snapshot is reloaded as before.
  const [following, setFollowing] = useState(false);
  const [openMode, setOpenMode] = useState(false);
  const pendingStart = useRef<{ at: number; play: boolean } | null>(null);
  // hls.js holds the element: no proxy src is set on it until it has let go (the switch to the final proxy).
  const [hlsActive, setHlsActive] = useState(false);
  const follow = openMode && Boolean(followUrl);
  const wantUrl = liveUrl ? (follow ? followUrl : liveUrl) : null;
  const hlsLoaded = useRef<string | null>(null);
  useEffect(() => () => { hls.current?.destroy(); hls.current = null; hlsLoaded.current = null; }, [videoEl]);
  useEffect(() => {
    const v = videoEl;
    if (!v) return;
    if (!wantUrl) {
      // The live link went away (the final proxy is there): hls.js lets go before the proxy is set, at the same place.
      if (hls.current) { resumeAt.current = v.currentTime; hls.current.destroy(); hls.current = null; hlsLoaded.current = null; }
      setFollowing(false);
      setOpenMode(false);
      setHlsActive(false);
      return;
    }
    const sameKind = Boolean(hls.current && hls.current.follow === follow);
    // A newer signed link to what is loaded: hls.js takes up its token for later requests, with no reload.
    if (sameKind && !usesNativeHls(v)) { hls.current!.setUrl(wantUrl); hlsLoaded.current = wantUrl; return; }
    const apply = () => {
      if (hlsLoaded.current === wantUrl) return;
      const start = pendingStart.current;
      pendingStart.current = null;
      const at = start?.at ?? (hls.current ? v.currentTime : null);
      const play = start?.play ?? (hls.current ? !v.paused : false);
      hls.current?.destroy();
      hls.current = attachHls(v, wantUrl, { onError: setError, follow, startAt: at, play });
      hlsLoaded.current = wantUrl;
      setHlsActive(true);
    };
    // Safari's own player takes up a new link when it pauses.
    if (!hls.current || v.paused || !sameKind) { apply(); return; }
    v.addEventListener('pause', apply, { once: true });
    return () => v.removeEventListener('pause', apply);
  }, [videoEl, wantUrl, follow]);
  // The snapshot, as it grows: loaded again when the playhead is within a few seconds of the end of what is loaded.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !hls.current || hls.current.follow || !Number.isFinite(v.duration) || recordedSeconds - v.duration < 2) return;
    if (v.duration - v.currentTime < 5) hls.current.refresh();
  }, [recordedSeconds]);

  const tcAt = useCallback(
    (sec: number) => {
      // The end of media is the start of a frame that does not exist; show the last real frame.
      const clamped = durationSec > 0 && sec >= durationSec - frameSec / 2 ? Math.max(0, durationSec - frameSec / 2) : sec;
      return formatTimecode(framesToTimecode(startFrames + ticksToFrames(secondsToTicks(clamped), rate), rate));
    },
    [rate, startFrames, durationSec, frameSec],
  );

  useImperativeHandle(ref, () => ({
    seek: (s) => { if (videoRef.current) videoRef.current.currentTime = s; },
    element: () => videoRef.current,
    range: () => ({ in: inPoint, out: outPoint }),
    setRange: (inSec, outSec) => {
      const i = inSec != null && Number.isFinite(inSec) ? Math.max(0, inSec) : null;
      const o = outSec != null && Number.isFinite(outSec) ? Math.max(0, outSec) : null;
      setInPoint(i);
      setOutPoint(i != null && o != null && o <= i ? null : o);
      setFollowing(false);
    },
    playRange: (fromSec, toSec) => {
      const v = videoRef.current;
      if (!v || !(toSec > fromSec)) return;
      playUntil.current?.stop();
      stopReverse();
      setFollowing(false);
      v.currentTime = fromSec;
      v.playbackRate = 1;
      setSpeed(1);
      let raf = 0;
      const check = () => {
        if (v.currentTime >= toSec - 0.01 || v.paused) { stop(); if (!v.paused) v.pause(); return; }
        raf = requestAnimationFrame(check);
      };
      const stop = () => { cancelAnimationFrame(raf); if (playUntil.current?.stop === stop) playUntil.current = null; };
      playUntil.current = { to: toSec, stop };
      void v.play().then(() => { raf = requestAnimationFrame(check); }).catch(() => stop());
    },
  }), [inPoint, outPoint]);

  const flash = (text: string) => {
    setOsd(text);
    if (osdTimer.current) clearTimeout(osdTimer.current);
    osdTimer.current = window.setTimeout(() => setOsd(null), 900);
  };

  // Smooth time updates while playing.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let raf = 0;
    const tick = () => {
      setTime(v.currentTime);
      raf = requestAnimationFrame(tick);
    };
    if (playing) raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = volume;
    v.muted = muted;
  }, [volume, muted]);

  const stopReverse = () => {
    if (reverseTimer.current) {
      clearInterval(reverseTimer.current);
      reverseTimer.current = null;
    }
  };

  const seek = useCallback((sec: number) => {
    const v = videoRef.current;
    if (!v) return;
    const t = Math.max(0, Math.min(durationSec || v.duration || 0, sec));
    // Scrubbing back stops following the live edge.
    if (!atLiveEdge(recordedSeconds, t)) setFollowing(false);
    v.currentTime = t;
    setTime(t);
  }, [durationSec, recordedSeconds]);

  /** 1.9.1: Go live (the button, or End while recording): just before the end of what is recorded, playing and following it. */
  const goLive = useCallback(() => {
    const v = videoRef.current;
    if (!v || !recording) return;
    stopReverse();
    playUntil.current?.stop();
    v.playbackRate = 1;
    setSpeed(1);
    const target = goLiveTarget(recordedSeconds);
    setFollowing(true);
    if (hls.current?.follow || !followUrl) {
      const end = Number.isFinite(v.duration) && v.duration > 0 ? v.duration : recordedSeconds;
      v.currentTime = Math.min(target, Math.max(0, end - GO_LIVE_BACKOFF_SECONDS));
      void v.play().catch(() => undefined);
    } else {
      pendingStart.current = { at: target, play: true };
      setOpenMode(true);
    }
  }, [recording, recordedSeconds, followUrl]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    stopReverse();
    if (v.paused) {
      v.playbackRate = speed;
      void v.play().catch(() => undefined);
    } else { v.pause(); setFollowing(false); }
  }, [speed]);

  const shuttle = useCallback((dir: 1 | -1) => {
    const v = videoRef.current;
    if (!v) return;
    if (dir === 1) {
      stopReverse();
      const next = v.paused || v.playbackRate < 1 ? 1 : Math.min(8, v.playbackRate * 2);
      v.playbackRate = next;
      setSpeed(next);
      void v.play().catch(() => undefined);
      flash(`▶ ${next}×`);
    } else {
      // Browsers cannot decode backwards; emulate reverse shuttle with repeated seeks.
      v.pause();
      setFollowing(false);
      const current = reverseTimer.current ? (Number(rootRef.current?.dataset.reverse) || 1) * 2 : 1;
      const r = Math.min(8, current);
      if (rootRef.current) rootRef.current.dataset.reverse = String(r);
      stopReverse();
      reverseTimer.current = window.setInterval(() => {
        if (v.currentTime <= 0) return stopReverse();
        v.currentTime = Math.max(0, v.currentTime - 0.1 * r);
        setTime(v.currentTime);
      }, 100);
      flash(`◀ ${r}×`);
    }
  }, []);

  const step = useCallback((frames: number) => {
    const v = videoRef.current;
    if (!v) return;
    stopReverse();
    v.pause();
    setFollowing(false);
    // Land in the middle of the target frame to avoid boundary rounding.
    const current = Math.floor(v.currentTime / frameSec + 1e-6);
    seek((current + frames) * frameSec + frameSec / 2);
  }, [frameSec, seek]);

  // 1.5.0: the asset's shots, for the ticks, ↑ / ↓ and the jump buttons (S2's ShotTicks.tsx).
  const jumps = useShotJumps(asset, () => videoRef.current?.currentTime ?? 0, seek);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void rootRef.current?.requestFullscreen();
  }, []);

  useEffect(() => {
    const onFs = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  useEffect(() => stopReverse, []);

  useEffect(() => { onRangeChange?.({ in: inPoint, out: outPoint }); }, [inPoint, outPoint, onRangeChange]);
  const lastReport = useRef(0);
  useEffect(() => {
    // Throttled: listeners (subtitle lists, editors) do not need every animation frame.
    if (!onTimeChange) return;
    const now = performance.now();
    if (!playing || now - lastReport.current > 200) { lastReport.current = now; onTimeChange(time); }
  }, [time, onTimeChange, playing]);

  // Keyboard shortcuts (active on the asset page unless typing in a field).
  useEffect(() => {
    if (!keyboard) return;
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e) || e.metaKey || e.ctrlKey) return;
      const v = videoRef.current;
      if (!v) return;
      const k = e.key;
      const lower = k.toLowerCase();
      if (k === ' ' || (lower === 'k' && !e.altKey)) {
        e.preventDefault();
        if (lower === 'k') { stopReverse(); v.pause(); setFollowing(false); v.playbackRate = 1; setSpeed(1); } else togglePlay();
      } else if (lower === 'l' && !e.altKey) { e.preventDefault(); shuttle(1); }
      else if (lower === 'j' && !e.altKey) { e.preventDefault(); shuttle(-1); }
      else if (k === 'ArrowRight') { e.preventDefault(); if (e.shiftKey) seek(v.currentTime + 1); else step(1); }
      else if (k === 'ArrowLeft') { e.preventDefault(); if (e.shiftKey) seek(v.currentTime - 1); else step(-1); }
      // 1.5.0: previous and next shot (only with shots).
      else if (k === 'ArrowUp' && !e.shiftKey && !e.altKey && jumps.shots) { e.preventDefault(); jumps.prev(); }
      else if (k === 'ArrowDown' && !e.shiftKey && !e.altKey && jumps.shots) { e.preventDefault(); jumps.next(); }
      else if (lower === 'i' && !e.altKey) {
        e.preventDefault();
        if (e.shiftKey) { if (inPoint != null) seek(inPoint); }
        else { setFollowing(false); setInPoint(v.currentTime); if (outPoint != null && outPoint <= v.currentTime) setOutPoint(null); flash(`IN ${tcAt(v.currentTime)}`); }
      } else if (lower === 'o' && !e.altKey) {
        e.preventDefault();
        if (e.shiftKey) { if (outPoint != null) seek(outPoint); }
        else { setFollowing(false); setOutPoint(v.currentTime); if (inPoint != null && inPoint >= v.currentTime) setInPoint(null); flash(`OUT ${tcAt(v.currentTime)}`); }
      } else if (e.altKey && (lower === 'x' || e.code === 'KeyX')) { e.preventDefault(); setInPoint(null); setOutPoint(null); flash('Cleared in/out'); }
      // 1.9.0: not with ⌥ (⌥M merges cues in the Subtitles tab; Option changes e.key on a Mac, not elsewhere).
      else if (lower === 'm' && !e.altKey) { setMuted(!muted); flash(muted ? 'Sound on' : 'Muted'); }
      else if (lower === 'f' && !e.altKey) toggleFullscreen();
      else if (k === 'Home') seek(0);
      // 1.9.1: End on a recording goes live (and follows it); otherwise to the end.
      else if (k === 'End') { if (recording) { e.preventDefault(); goLive(); } else seek(durationSec); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [keyboard, togglePlay, shuttle, step, seek, inPoint, outPoint, muted, setMuted, toggleFullscreen, durationSec, tcAt, jumps, recording, goLive]);

  const range = inPoint != null || outPoint != null ? { a: inPoint ?? 0, b: outPoint ?? durationSec } : null;
  // Programme timecodes are source timecode: seconds from the file's start timecode.
  // 1.9.1: a timecode earlier than the start is on the next day (a recording past midnight).
  const toSec = (tc: string | null | undefined) => programmeSeconds(tc, rate, startFrames, durationSec);
  const marks = { in: toSec(programme?.in), out: toSec(programme?.out) };

  return (
    <div className={`player ${compact ? 'compact' : ''}`} ref={rootRef} data-testid="player">
      <div className="player-stage" onClick={togglePlay} onDoubleClick={toggleFullscreen}>
        {proxySrc || liveUrl ? (
          <>
            <video
              ref={(el) => { videoRef.current = el; setVideoEl(el); }}
              src={liveUrl || hlsActive ? undefined : proxySrc ?? undefined}
              crossOrigin={liveUrl ? 'anonymous' : undefined}
              data-live={liveUrl ? 'true' : undefined}
              poster={posterSrc ?? undefined}
              preload="auto"
              playsInline
              data-testid="proxy-video"
              style={isAudio ? { position: 'absolute', opacity: 0, pointerEvents: 'none' } : undefined}
              onPlay={() => setPlaying(true)}
              onPause={() => { setPlaying(false); setTime(videoRef.current?.currentTime ?? 0); }}
              onSeeked={() => setTime(videoRef.current?.currentTime ?? 0)}
              onTimeUpdate={() => !playing && setTime(videoRef.current?.currentTime ?? 0)}
              onDurationChange={(e) => { const d = e.currentTarget.duration; if (Number.isFinite(d) && d > 0) setMediaDuration(d); }}
              onEnded={() => setFollowing(false)}
              onLoadedMetadata={(e) => {
                setMediaDuration(e.currentTarget.duration);
                setError(null);
                if (resumeAt.current != null) { e.currentTarget.currentTime = resumeAt.current; resumeAt.current = null; }
              }}
              onProgress={(e) => {
                const b = e.currentTarget.buffered;
                if (b.length) setBuffered(b.end(b.length - 1));
              }}
              onWaiting={() => setWaiting(true)}
              onPlaying={() => setWaiting(false)}
              onCanPlay={() => setWaiting(false)}
              onError={() => {
                if (liveUrl) return;
                resumeAt.current = videoRef.current?.currentTime ?? null;
                if (!refreshProxy()) setError('The proxy could not be loaded. Its storage may be offline; PRISM will retry when the asset refreshes.');
              }}
            />
            {isAudio && <div className="audio-visual"><AudioLines size={64} color="var(--accent)" strokeWidth={1.2} /></div>}
          </>
        ) : null}
        <div className="overlay-badges">
          {live && <span className="pill rec" data-testid="source-rec" title="Recording: what is recorded so far plays from the live proxy">● REC · {formatDuration(secondsToTicks(live.recordedSeconds))} so far</span>}
          {!live && <span className="pill" title="Browser playback uses a proxy; the original is preserved in storage">PROXY{asset.representations.find((r) => r.kind === 'proxy')?.attributes.height ? ` · ${asset.representations.find((r) => r.kind === 'proxy')?.attributes.height}p` : ''}</span>}
          {asset.timing.normalizedFromVfr && <span className="pill" title="The source has a variable frame rate; the proxy was normalised to a constant rate">VFR → CFR</span>}
        </div>
        {osd && <div className="osd">{osd}</div>}
        <SubtitleOverlay track={subtitleEditing ? { cues: subtitleEditing.cues, style: subtitleEditing.style } : subtitleTracks.find((t) => t.id === subtitleId) ?? null} seconds={time} video={isAudio ? null : videoEl} />
        {(waiting || error) && (
          <div className="center-state">
            {error ? <div className="banner error" style={{ maxWidth: 420 }}>{error}</div> : <div className="spin" style={{ width: 28, height: 28, border: '2px solid rgba(255,255,255,.2)', borderTopColor: 'var(--accent)', borderRadius: '50%' }} />}
          </div>
        )}
      </div>
      <div className="transport">
        <Timeline
          duration={durationSec}
          time={time}
          buffered={buffered}
          range={range}
          waveformUrl={waveSrc}
          waveformKey={waveRep?.id ?? null}
          spriteUrl={spriteSrc}
          spriteLayout={(asset.representations.find((r) => r.kind === 'sprite')?.attributes as SpriteLayout | undefined) ?? null}
          tcAt={tcAt}
          onSeek={seek}
          marks={marks}
          strip={!compact}
        >
          {live && !waveSrc && !spriteSrc && <div className="timeline-pending" data-testid="timeline-pending">Waveform and filmstrip appear when the recording ends</div>}
          <ShotTicks jumps={jumps} duration={durationSec} />
          {subtitleEditing && <CueTicks cues={subtitleEditing.cues} selectedIds={subtitleEditing.selectedIds} duration={durationSec} trackStyle={subtitleEditing.style} />}
        </Timeline>
        <div className="controls">
          <button className="btn ghost icon" onClick={() => step(-1)} aria-label="Previous frame" title="Previous frame (←)"><ChevronLeft size={18} /></button>
          <button className="btn icon" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'} title="Play/pause (Space)" data-testid="play-toggle">
            {playing ? <Pause size={16} /> : <Play size={16} />}
          </button>
          <button className="btn ghost icon" onClick={() => step(1)} aria-label="Next frame" title="Next frame (→)"><ChevronRight size={18} /></button>
          <ShotJumpButtons jumps={jumps} />
          <span className="tc-display" data-testid="timecode" title="Source timecode (preview position is approximate)">{tcAt(time)}</span>
          <span className="tc-duration">/ {formatDuration(secondsToTicks(durationSec))}</span>
          {/* 1.9.1: while recording, LIVE at the edge or "−m:ss · Go live"; once it has ended, until the proxy is made. */}
          {recording && <LiveEdgeButton atEdge={following && atLiveEdge(recordedSeconds, time)} behind={behindSeconds(recordedSeconds, time)} onGoLive={goLive} className="in-controls" />}
          {live?.state === 'finishing' && <span className="live-finishing" data-testid="source-finishing" title="The stream has ended; the recording is being processed. The final proxy replaces this preview by itself.">Recording ended · processing…</span>}
          {range && (
            <span className="inout">
              IN <b>{tcAt(range.a)}</b> OUT <b>{tcAt(range.b)}</b>
              <span className="faint">({formatDuration(secondsToTicks(range.b - range.a))})</span>
              <button className="btn ghost icon sm" aria-label="Clear in and out" onClick={() => { setInPoint(null); setOutPoint(null); }}><X size={13} /></button>
              {onSetProgramme && (
                <button className="btn ghost sm" title="Save the marked in and out as the programme’s in and out timecodes" data-testid="set-programme"
                  onClick={() => onSetProgramme({ in: inPoint != null ? tcAt(inPoint) : null, out: outPoint != null ? tcAt(outPoint) : null })}>Set as programme</button>
              )}
            </span>
          )}
          {!range && (marks.in != null || marks.out != null) && (
            <span className="inout" title="Programme in and out from the metadata">
              PGM
              <button className="btn ghost sm mono" disabled={marks.in == null} onClick={() => marks.in != null && seek(marks.in)}>{programme?.in ?? 'start'}</button>–
              <button className="btn ghost sm mono" disabled={marks.out == null} onClick={() => marks.out != null && seek(marks.out)}>{programme?.out ?? 'end'}</button>
            </span>
          )}
          <span className="spacer" />
          {!compact && <ShowShotsToggle jumps={jumps} />}
          {subtitleEditing && <span className="pill" title="The Subtitles tab is open: the player shows the track you are editing, unsaved changes included" data-testid="subtitle-editing-pill">Editing subtitles</span>}
          {subtitleTracks.length > 0 && !subtitleEditing && (
            <select className="speed-select" aria-label="Subtitles" value={subtitleId} onChange={(e) => setSubtitleId(e.target.value)} data-testid="subtitle-select">
              <option value="">Subtitles off</option>
              {subtitleTracks.map((t) => <option key={t.id} value={t.id}>{t.label} ({t.language})</option>)}
            </select>
          )}
          <select className="speed-select" aria-label="Playback speed" value={speed} onChange={(e) => { const s = Number(e.target.value); setSpeed(s); if (videoRef.current) videoRef.current.playbackRate = s; }}>
            {SPEEDS.map((s) => <option key={s} value={s}>{s}×</option>)}
          </select>
          <div className="volume">
            <button className="btn ghost icon sm" aria-label={muted ? 'Unmute' : 'Mute'} onClick={() => setMuted(!muted)}>
              {muted || volume === 0 ? <VolumeX size={16} /> : volume < 0.5 ? <Volume1 size={16} /> : <Volume2 size={16} />}
            </button>
            <input type="range" min={0} max={1} step={0.05} value={muted ? 0 : volume} aria-label="Volume" onChange={(e) => { setVolume(Number(e.target.value)); setMuted(false); }} />
          </div>
          <button className="btn ghost icon" onClick={toggleFullscreen} aria-label="Fullscreen" title="Fullscreen (F)">{fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}</button>
        </div>
      </div>
      {subtitleEditing && !fullscreen && (
        <SubtitleStrip cues={subtitleEditing.cues} trackStyle={subtitleEditing.style} rate={subtitleEditing.rate} startFrames={subtitleEditing.startFrames}
          durationTicks={secondsToTicks(durationSec)} playheadTicks={secondsToTicks(time)} selectedIds={subtitleEditing.selectedIds}
          windowSeconds={Math.min(STRIP_MAX_SECONDS, Math.max(STRIP_MIN_SECONDS, Number(stripSeconds) || STRIP_DEFAULT_SECONDS))} onWindowSeconds={setStripSeconds}
          onSeek={(t) => seek(t / 705_600_000)} onSelect={subtitleEditing.onSelect} onCues={subtitleEditing.onCues}
          waveformUrl={waveSrc} waveformKey={waveRep?.id ?? null} readOnly={subtitleEditing.readOnly} />
      )}
    </div>
  );
});
